/**
 * Fixed, code-level enumerations shared by the API (DB enums, validation) and the web app (labels, pickers).
 * Anything a salesperson or admin may want to rename/reorder (pipeline stages, relationship statuses, tiers,
 * scoring weights, lead sources, product categories) lives in the database instead — see docs/ARCHITECTURE.md.
 */

export const USER_ROLES = ['admin', 'manager', 'sales_rep', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

/**
 * What a business is to us. Pizzerias (and other restaurants) are the leads we sell ovens to; dealers resell our
 * equipment; reps are independent sales agencies that represent our lines in a territory. Each kind has its own
 * list (Leads / Dealers / Reps). Pizzeria fit scoring, tiers, pizza focus and installed equipment only apply to
 * pizzerias; everything else (contacts, calls, email, tasks, calendar, pipeline, follow-ups) applies to all.
 */
export const BUSINESS_KINDS = ['pizzeria', 'dealer', 'rep'] as const;
export type BusinessKind = (typeof BUSINESS_KINDS)[number];

/**
 * Where a pizzeria sits, moved by hand: 'lead' (the Leads list), 'prospect' (engaged — the Prospects list),
 * 'client' (already a customer — the Clients list) or 'test_kitchen' (a kitchen trialling our equipment — the
 * Test Kitchens list). Dealers and reps keep 'lead' and ignore it.
 */
export const LEAD_SEGMENTS = ['lead', 'prospect', 'client', 'test_kitchen'] as const;
export type LeadSegment = (typeof LEAD_SEGMENTS)[number];

export const OWNERSHIP_TYPES = ['independent', 'chain', 'franchise', 'group', 'unknown'] as const;
export type OwnershipType = (typeof OWNERSHIP_TYPES)[number];

export const OPERATING_STATUSES = [
  'operating',
  'coming_soon',
  'temporarily_closed',
  'permanently_closed',
  'unknown',
] as const;
export type OperatingStatus = (typeof OPERATING_STATUSES)[number];

/** How central pizza is to the business. Drives the "pizza-focused" fit rule. */
export const PIZZA_FOCUS = ['primary', 'secondary', 'none', 'unknown'] as const;
export type PizzaFocus = (typeof PIZZA_FOCUS)[number];

export const PURCHASE_TIMEFRAMES = [
  'immediate',
  'within_3_months',
  'within_6_months',
  'within_12_months',
  'over_12_months',
  'unknown',
] as const;
export type PurchaseTimeframe = (typeof PURCHASE_TIMEFRAMES)[number];

export const CONTACT_ROLES = [
  'owner',
  'founder',
  'general_manager',
  'kitchen_manager',
  'operations_manager',
  'purchasing_manager',
  'chef',
  'other_decision_maker',
  'staff',
  'other',
] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export const DECISION_MAKER_ROLES: readonly ContactRole[] = [
  'owner',
  'founder',
  'general_manager',
  'operations_manager',
  'purchasing_manager',
  'other_decision_maker',
];

export const CONTACT_VERIFICATION_STATUSES = ['unverified', 'verified', 'outdated', 'invalid'] as const;
export type ContactVerificationStatus = (typeof CONTACT_VERIFICATION_STATUSES)[number];

export const PREFERRED_CONTACT_METHODS = ['email', 'phone', 'sms', 'in_person', 'any'] as const;
export type PreferredContactMethod = (typeof PREFERRED_CONTACT_METHODS)[number];

/**
 * Legal basis for commercial email. Used by the compliance policy (e.g. CASL mode requires one of the
 * non-"none" bases, and implied bases expire).
 */
export const CONSENT_BASES = [
  'none',
  'implied_published',
  'implied_business_relationship',
  'implied_inquiry',
  'express',
  'withdrawn',
] as const;
export type ConsentBasis = (typeof CONSENT_BASES)[number];
/** What an import can say about where its email addresses came from (never 'withdrawn'). */
export const IMPORT_CONSENT_BASES = ['none', 'implied_published', 'implied_business_relationship', 'implied_inquiry', 'express'] as const;
export type ImportConsentBasis = (typeof IMPORT_CONSENT_BASES)[number];

/** Semantic kind of a configurable pipeline stage. */
export const STAGE_KINDS = ['open', 'won', 'lost', 'dormant', 'do_not_contact'] as const;
export type StageKind = (typeof STAGE_KINDS)[number];

/**
 * Built-in stage keys the system understands (auto-advance on outreach/reply/opportunity changes).
 * Admins may rename, recolor, reorder or deactivate them, and add custom stages, but not delete them.
 */
export const SYSTEM_STAGE_KEYS = [
  'new',
  'researching',
  'qualified',
  'outreach_ready',
  'contacted',
  'responded',
  'conversation',
  'opportunity',
  'quote_requested',
  'quote_sent',
  'negotiation',
  'won',
  'lost',
  'dormant',
  'do_not_contact',
] as const;
export type SystemStageKey = (typeof SYSTEM_STAGE_KEYS)[number];

export const SYSTEM_RELATIONSHIP_KEYS = [
  'unknown',
  'cold',
  'contacted',
  'responded',
  /** A reply that shows interest (asks for pricing, a call, a demo…); set automatically by the reply scan. */
  'interested',
  'warm',
  'active_conversation',
  'established',
  'customer',
  'former_customer',
  'lost',
  'do_not_contact',
] as const;
export type SystemRelationshipKey = (typeof SYSTEM_RELATIONSHIP_KEYS)[number];

export const SCORE_CATEGORIES = ['business_fit', 'equipment', 'engagement', 'relationship', 'negative'] as const;
export type ScoreCategory = (typeof SCORE_CATEGORIES)[number];

export const SCORE_CATEGORY_LABELS: Record<ScoreCategory, string> = {
  business_fit: 'Business fit',
  equipment: 'Equipment opportunity',
  engagement: 'Engagement',
  relationship: 'Relationship',
  negative: 'Negative signals',
};

/**
 * attribute: evaluated from the business record (pizza focus, ownership, locations, equipment age...).
 * signal: evaluated from recorded lead_score_events (inquiry, quote request, reply, meeting...).
 * tag: +points when the business carries a configured tag (admin-defined, no code needed).
 */
export const SCORING_RULE_KINDS = ['attribute', 'signal', 'tag'] as const;
export type ScoringRuleKind = (typeof SCORING_RULE_KINDS)[number];

export const OPPORTUNITY_STAGES = [
  'qualifying',
  'quote_requested',
  'quote_sent',
  'negotiation',
  'won',
  'lost',
] as const;
export type OpportunityStage = (typeof OPPORTUNITY_STAGES)[number];
export const OPEN_OPPORTUNITY_STAGES: readonly OpportunityStage[] = [
  'qualifying',
  'quote_requested',
  'quote_sent',
  'negotiation',
];

export const ACTIVITY_TYPES = [
  'lead_created',
  'lead_discovered',
  'lead_imported',
  'lead_reviewed',
  'lead_updated',
  'lead_merged',
  'lead_deleted',
  'lead_restored',
  'lead_verified',
  'stage_changed',
  'relationship_changed',
  'owner_changed',
  'tier_changed',
  'do_not_contact_set',
  'do_not_contact_cleared',
  'contact_added',
  'contact_updated',
  'contact_removed',
  'email_scheduled',
  'email_sent',
  'email_received',
  'email_bounced',
  'email_unsubscribed',
  'email_cancelled',
  'call',
  'meeting',
  'note_added',
  'task_created',
  'task_completed',
  'opportunity_created',
  'opportunity_updated',
  'opportunity_stage_changed',
  'opportunity_won',
  'opportunity_lost',
  'quote_requested',
  'quote_sent',
  'signal_recorded',
  'signal_voided',
  'sequence_enrolled',
  'sequence_paused',
  'sequence_resumed',
  'sequence_stopped',
  'sequence_completed',
  'other',
] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ACTOR_TYPES = ['user', 'system', 'automation', 'import', 'integration'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

export const CALL_OUTCOMES = ['connected', 'left_voicemail', 'no_answer', 'busy', 'wrong_number'] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export const MEETING_OUTCOMES = ['held', 'scheduled', 'no_show', 'cancelled'] as const;
export type MeetingOutcome = (typeof MEETING_OUTCOMES)[number];

export const TASK_TYPES = ['follow_up', 'call', 'email', 'meeting', 'reminder', 'todo', 'note'] as const;
export type TaskType = (typeof TASK_TYPES)[number];

/**
 * Calendar-only entries: informational notes on a day or time ("Owner away until the 12th", "Trade show").
 * They are never completed and never overdue, and they are not work: task lists, task counts, the dashboard and
 * the next best action ignore them. They always have a date.
 */
export const CALENDAR_ONLY_TASK_TYPES: readonly TaskType[] = ['note'];

export const RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly', 'yearly'] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

/** Monthly repeats on the first occurrence's day of the month, its nth weekday ("2nd Tuesday") or its last weekday. */
export const MONTHLY_RECURRENCE_MODES = ['day_of_month', 'weekday_of_month', 'last_weekday_of_month'] as const;
export type MonthlyRecurrenceMode = (typeof MONTHLY_RECURRENCE_MODES)[number];

/** Which occurrences of a repeating entry an edit or delete applies to (Google Calendar semantics). */
export const RECURRENCE_EDIT_SCOPES = ['this', 'following', 'all'] as const;
export type RecurrenceEditScope = (typeof RECURRENCE_EDIT_SCOPES)[number];

/** Upper bound for "ends after N occurrences". */
export const MAX_RECURRENCE_COUNT = 730;

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_STATUSES = ['open', 'completed', 'cancelled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const EMAIL_DIRECTIONS = ['outbound', 'inbound'] as const;
export type EmailDirection = (typeof EMAIL_DIRECTIONS)[number];

/**
 * Outbound lifecycle: draft → pending_approval → scheduled → sending → sent → delivered | bounced
 * failed = provider definitively rejected; uncertain = worker died mid-send, needs human review (never auto-resent).
 * Inbound messages use `received`.
 */
export const EMAIL_STATUSES = [
  'draft',
  'pending_approval',
  'scheduled',
  'sending',
  'sent',
  'delivered',
  'bounced',
  'failed',
  'cancelled',
  'uncertain',
  'received',
] as const;
export type EmailStatus = (typeof EMAIL_STATUSES)[number];

export const EMAIL_SOURCES = ['manual', 'bulk', 'automation', 'inbound'] as const;

/** How a person's own mailbox is authenticated: Google sign-in (send only), password (SMTP/IMAP), or Microsoft sign-in. */
export const EMAIL_ACCOUNT_PROVIDERS = ['google', 'smtp', 'microsoft'] as const;
export type EmailAccountProvider = (typeof EMAIL_ACCOUNT_PROVIDERS)[number];
export const EMAIL_ACCOUNT_STATUSES = ['active', 'error'] as const;
export type EmailAccountStatus = (typeof EMAIL_ACCOUNT_STATUSES)[number];
export type EmailSource = (typeof EMAIL_SOURCES)[number];

export const EMAIL_EVENT_TYPES = [
  'delivered',
  'bounced',
  'opened',
  'clicked',
  'complained',
  'unsubscribed',
  'replied',
] as const;
export type EmailEventType = (typeof EMAIL_EVENT_TYPES)[number];

/** What a lead's reply says, read from its wording: moves the lead to Potentially interested, Lost, or unsubscribes it. */
export const REPLY_INTENTS = ['interested', 'not_interested', 'unsubscribe', 'neutral'] as const;
export type ReplyIntent = (typeof REPLY_INTENTS)[number];

export const SUPPRESSION_REASONS = [
  'unsubscribed',
  'hard_bounce',
  'complaint',
  'do_not_contact',
  'invalid_address',
  'manual',
] as const;
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number];

export const ENROLLMENT_STATUSES = ['active', 'paused', 'completed', 'stopped'] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export const AUTOMATION_DECISIONS = [
  'enrolled',
  'email_queued',
  'skipped',
  'paused',
  'resumed',
  'stopped',
  'completed',
  'blocked_at_send',
] as const;
export type AutomationDecision = (typeof AUTOMATION_DECISIONS)[number];

export const EMAIL_TEMPLATE_CATEGORIES = [
  'introduction',
  'equipment_replacement',
  'new_restaurant',
  'quote_follow_up',
  'check_in',
  'new_location',
  'post_meeting',
  'custom',
] as const;
export type EmailTemplateCategory = (typeof EMAIL_TEMPLATE_CATEGORIES)[number];

export const CAMPAIGN_STATUSES = ['scheduled', 'sending', 'completed', 'cancelled'] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

export const AUTOMATION_RUN_STATUSES = ['running', 'completed', 'failed', 'skipped'] as const;
export type AutomationRunStatus = (typeof AUTOMATION_RUN_STATUSES)[number];

/** Outcome of one provider call. `uncertain` = we cannot know whether the provider accepted the message. */
export const SEND_ATTEMPT_OUTCOMES = ['sent', 'retryable_error', 'permanent_error', 'uncertain'] as const;
export type SendAttemptOutcome = (typeof SEND_ATTEMPT_OUTCOMES)[number];

export const IMPORT_STATUSES = [
  'uploaded',
  'validating',
  'validated',
  'importing',
  'completed',
  'failed',
  'cancelled',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

export const IMPORT_ROW_STATUSES = [
  'pending',
  'valid',
  'invalid',
  /** Parsed fine but not a usable lead (placeholder name, no way to reach or find it, not a food business…). */
  'junk',
  'duplicate',
  'imported',
  'merged',
  'skipped',
  'failed',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

export const DUPLICATE_RESOLUTIONS = ['create', 'merge', 'skip'] as const;
export type DuplicateResolution = (typeof DUPLICATE_RESOLUTIONS)[number];

export const DISCOVERY_SEARCH_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type DiscoverySearchStatus = (typeof DISCOVERY_SEARCH_STATUSES)[number];

export const DISCOVERY_REVIEW_STATUSES = ['pending', 'imported', 'merged', 'rejected'] as const;
export type DiscoveryReviewStatus = (typeof DISCOVERY_REVIEW_STATUSES)[number];

/**
 * Outcome of reading a business's own website for the email address it publishes:
 * found = an address was found; none = the pages were read but show no usable address; unreachable = the site did
 * not answer, errored or is not a web page; blocked = robots.txt asks crawlers not to read the pages.
 */
export const WEBSITE_LOOKUP_STATUSES = ['found', 'none', 'unreachable', 'blocked'] as const;
export type WebsiteLookupStatus = (typeof WEBSITE_LOOKUP_STATUSES)[number];

/** How an address appeared on the page. */
export const WEBSITE_EMAIL_SOURCES = ['mailto', 'text', 'obfuscated', 'cloudflare', 'structured_data'] as const;
export type WebsiteEmailSource = (typeof WEBSITE_EMAIL_SOURCES)[number];

export const DUPLICATE_CANDIDATE_STATUSES = ['pending', 'merged', 'kept_separate', 'ignored'] as const;
export type DuplicateCandidateStatus = (typeof DUPLICATE_CANDIDATE_STATUSES)[number];

export const PROPOSAL_STATUSES = ['pending', 'accepted', 'rejected'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

export const JOB_STATUSES = ['queued', 'running', 'completed', 'failed', 'dead'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const CUSTOM_FIELD_ENTITIES = ['business', 'contact', 'opportunity'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export const CUSTOM_FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'select'] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const LEAD_SOURCE_KINDS = ['manual', 'csv_import', 'discovery', 'referral', 'event', 'inbound', 'other'] as const;
export type LeadSourceKind = (typeof LEAD_SOURCE_KINDS)[number];

export const EQUIPMENT_TYPES = [
  'deck_oven',
  'conveyor_oven',
  'wood_fired_oven',
  'gas_brick_oven',
  'electric_oven',
  'rotating_oven',
  'combi_oven',
  'dough_mixer',
  'dough_sheeter',
  'dough_press',
  'dough_divider',
  'prep_table',
  'refrigeration',
  'proofer',
  'hood_ventilation',
  'other',
] as const;
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number];
export const OVEN_EQUIPMENT_TYPES: readonly EquipmentType[] = [
  'deck_oven',
  'conveyor_oven',
  'wood_fired_oven',
  'gas_brick_oven',
  'electric_oven',
  'rotating_oven',
  'combi_oven',
];

export const FUEL_TYPES = ['gas', 'electric', 'wood', 'coal', 'hybrid', 'unknown'] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

export const EQUIPMENT_CONDITIONS = ['new', 'good', 'fair', 'poor', 'unknown'] as const;
export type EquipmentCondition = (typeof EQUIPMENT_CONDITIONS)[number];

/** Suggested restaurant styles; the column is free text so teams can add their own. */
export const RESTAURANT_TYPE_SUGGESTIONS = [
  'Neapolitan',
  'Wood-fired',
  'New York style',
  'Chicago / deep dish',
  'Detroit style',
  'Roman / al taglio',
  'Fast casual',
  'Delivery & takeout',
  'Full-service Italian',
  'Pizza chain',
  'Food truck',
  'Bar & pizza',
  'Bakery / pizza by the slice',
  'Grocery / prepared foods',
  'Catering / commissary',
] as const;

export const BUSINESS_CATEGORY_SUGGESTIONS = [
  'Pizzeria',
  'Italian restaurant',
  'Restaurant',
  'Bar / pub',
  'Food truck',
  'Bakery',
  'Caterer',
  'Grocery / market',
  'Hotel / institutional',
  'Ghost kitchen',
] as const;

/** Next Best Action vocabulary (rules-based, see apps/api/src/domain/next-action.ts). */
export const NEXT_ACTIONS = [
  'do_not_contact',
  'review_duplicate',
  'complete_overdue_task',
  'respond_to_reply',
  'prepare_quote',
  'send_quote_follow_up',
  'contact_now',
  'call',
  'follow_up',
  'research_contact',
  'wait_until_scheduled',
  'nurture',
  'no_action',
] as const;
export type NextAction = (typeof NEXT_ACTIONS)[number];

export const NEXT_ACTION_LABELS: Record<NextAction, string> = {
  do_not_contact: 'Do not contact',
  review_duplicate: 'Review possible duplicate',
  complete_overdue_task: 'Complete overdue task',
  respond_to_reply: 'Respond to reply',
  prepare_quote: 'Prepare quote',
  send_quote_follow_up: 'Send quote follow-up',
  contact_now: 'Contact now',
  call: 'Call',
  follow_up: 'Follow up',
  research_contact: 'Research contact',
  wait_until_scheduled: 'Wait until scheduled follow-up',
  nurture: 'Nurture',
  no_action: 'No action required',
};

/** Notes: feedback tickets team members send to the administrators (feature ideas, problems, questions). */
export const TICKET_KINDS = ['feature', 'problem', 'question', 'other'] as const;
export type TicketKind = (typeof TICKET_KINDS)[number];

export const TICKET_STATUSES = ['new', 'planned', 'in_progress', 'done', 'declined'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
